import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert as AlertIcon,
  Resolve,
  ResolveFilled
} from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { simpleDate, simpleTime } from "@/utils/datetime";
import Badge from "@/components/ui/badge";
import { useUsers, useUsersMe } from "@/entities/users/hooks";
import { AlertData, DealData } from "@/pages/deals/types";
import { KanbanContext } from "@/pages/deals/components/KanbanBoard/KanbanProvider";
import { useAlertActions } from "./useAlertActions";
import { cn } from "@/utils/utils";
import { IconButton } from "@/components/ui/icon-button";

const TRANSLATION_KEY = "deals.alerts.popover.alert";

const formatAlertText = (text: string) =>
  text
    .split(/\*\*(.*?)\*\*/g)
    .map((part, index) =>
      index % 2 === 1 ? <strong key={index}>{part}</strong> : part
    );

interface Props {
  dealId: string;
  alert: AlertData;
  onAlertUpdate?: (newDealAlerts: AlertData[]) => void;
  isLastAlert?: boolean;
  hasBackground?: boolean;
}

const Alert: React.FC<Props> = ({
  dealId,
  alert,
  onAlertUpdate,
  isLastAlert = false,
  hasBackground = false
}) => {
  const { t } = useTranslation();
  const kanbanContext = useContext(KanbanContext);

  const { alert: alertText, dismissedBy, createdAt, isDismissed } = alert;
  const [dismissedByUserData] = useUsers(
    dismissedBy ? [dismissedBy] : []
  ).users;
  const userData = useUsersMe().data;

  const dismissedByName =
    dismissedByUserData?.id === userData?.id
      ? t("you")
      : dismissedByUserData?.displayName || t("dreamer");

  const refetchKanbanColumn = (updatedDeal: DealData) => {
    if (
      updatedDeal.stage &&
      kanbanContext?.columnRefetchFunctions[updatedDeal.stage]
    ) {
      kanbanContext?.columnRefetchFunctions[updatedDeal.stage]();
    }
  };

  const { isAnimating, shouldHide, handleCheck } = useAlertActions({
    alert,
    dealId,
    onAlertUpdate,
    refetchKanbanColumn,
    isLastAlert
  });

  if (shouldHide) {
    return null;
  }

  return (
    <div
      className={cn(
        "group p-2 flex gap-3 rounded-lg border  transition-colors duration-200 ease-in-out hover:bg-white-4 backdrop-blur-[10px]",
        isAnimating && "animate-bounce-out-down",
        hasBackground ? "border-[rgba(255,255,255,0.16)]" : "border-transparent"
      )}
      data-test={`deal-alert-${alert.id || dealId}`}
      style={
        hasBackground
          ? {
              background:
                "linear-gradient(96deg, rgba(242, 36, 44, 0.07) 1.03%, rgba(255, 255, 255, 0.02) 100%), rgba(255, 255, 255, 0.04)"
            }
          : undefined
      }
    >
      <IconContainer
        color={isDismissed ? "disabled" : "negative"}
        size="5"
        className={!isDismissed ? "drop-shadow-red-glow" : ""}
      >
        <AlertIcon />
      </IconContainer>
      <div className="flex flex-col gap-1">
        <div className="flex gap-[6px]">
          <Text
            className={cn(
              "whitespace-pre-line",
              isDismissed && "!text-disabled"
            )}
          >
            {formatAlertText(alertText)}
          </Text>
          <IconButton
            variant="iconNoBg"
            className="transition-all duration-200 ease-in-out opacity-0 group-hover:opacity-100"
            tooltipText={t(
              `${TRANSLATION_KEY}.actionTooltip.${isDismissed ? "open" : "close"}`
            )}
            disabled={isAnimating}
            onClick={(e) => {
              e.stopPropagation();
              handleCheck();
            }}
            size="md"
          >
            {isDismissed ? <ResolveFilled /> : <Resolve />}
          </IconButton>
        </div>
        {isDismissed && dismissedByName ? (
          <Badge
            text={`${t(`${TRANSLATION_KEY}.closedBy`)} ${dismissedByName}`}
            className="border-border-badge-default border-[0.5px] px-[6px] py-1"
            textVariant="smBodySecondary"
          />
        ) : (
          <Text variant="smBodyTertiary">{`${t(`${TRANSLATION_KEY}.sent`)} ${t("dateTime.on")} ${simpleDate(new Date(createdAt), "long", true)} ${t("dateTime.at")} ${simpleTime(new Date(createdAt))}`}</Text>
        )}
      </div>
    </div>
  );
};

export default Alert;
